import { Download, Search } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv } from '@/core/api';
import type { CabinetViewMode } from '../store/cabinetView';

/** Shared CRM-style list chrome for the cabinet "My Space" pages — a toolbar
 * (search + CSV export + view-mode toggle) and a footer (count + pagination).
 * The view-mode store factory + `viewModes` live in `store/cabinetView`. */

/** Centered (horizontally + vertically) empty state for non-table views. */
export function CabinetEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="text-muted-foreground flex min-h-[40vh] items-center justify-center text-center text-sm">
      {children}
    </div>
  );
}

interface ToolbarProps {
  view: CabinetViewMode;
  onViewChange: (view: CabinetViewMode) => void;
  modes: readonly ViewModeOption<CabinetViewMode>[];
  /** Optional debounced-search input. Omit for lists with no searchable text. */
  search?: { value: string; onChange: (value: string) => void; placeholder: string };
  /** Base path for CSV export (e.g. `/cabinet/salary/`); omit to hide Export. */
  exportBase?: string;
  exportParams?: Record<string, unknown>;
  exportLabel?: string;
}

export function CabinetListToolbar({
  view,
  onViewChange,
  modes,
  search,
  exportBase,
  exportParams,
  exportLabel = 'Export as CSV',
}: ToolbarProps) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {search ? (
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder}
            className="pl-8"
            aria-label={search.placeholder}
          />
        </div>
      ) : (
        <div />
      )}
      <div className="flex items-center gap-2">
        {exportBase ? (
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => {
              void exportCsv(exportBase, {
                ...exportParams,
                export: 'csv',
              });
            }}
            aria-label={exportLabel}
            title={exportLabel}
          >
            <Download className="size-4" />
          </Button>
        ) : null}
        <ViewModeToggle value={view} onChange={onViewChange} modes={modes} />
      </div>
    </div>
  );
}

interface FooterProps {
  count: number;
  page: number;
  onPageChange: (next: (p: number) => number) => void;
  hasPrev: boolean;
  hasNext: boolean;
  isFetching: boolean;
  /** [singular, plural] e.g. ['record', 'records']. */
  noun: [string, string];
}

export function CabinetListFooter({
  count,
  page,
  onPageChange,
  hasPrev,
  hasNext,
  isFetching,
  noun,
}: FooterProps) {
  if (count <= 0) return null;
  return (
    <div className="flex items-center justify-between">
      <p className="text-muted-foreground text-sm">
        {count} {count === 1 ? noun[0] : noun[1]}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={!hasPrev || isFetching}
          onClick={() => onPageChange((p) => Math.max(1, p - 1))}
        >
          Previous
        </Button>
        <span className="text-sm">Page {page}</span>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasNext || isFetching}
          onClick={() => onPageChange((p) => p + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
